import { useCallback } from 'react';
import { Button, Badge, cn, Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { GripVertical, Plus, Settings, Trash2 } from 'lucide-react';
import {
  BuilderQuestionField,
  QuestionGroupSettingsDialog,
  useQuestionGroup,
  useFormQuestions,
  getFormBuilderActions,
  formatConditionalBehaviorText,
} from '@/features/quote-and-apply-builder';
import { useSortableItem, DND_TYPES } from '@/features/quote-and-apply-builder/hooks/use-sortable-item';

interface BuilderQuestionGroupProps {
  section: PolicyFormSection;
  subsection: PolicyFormSubSection;
  group: PolicyFormQuestionGroup;
  index: number;
}

export function BuilderQuestionGroup({ section, subsection, group, index }: BuilderQuestionGroupProps) {
  const questions = useFormQuestions();
  const { removeGroup, moveGroup } = getFormBuilderActions();
  const {
    showSettings,
    pendingNewQuestionRef,
    isSelected,
    hasCondition,
    formatClasses,
    handleClick,
    handleAddQuestion,
    handleClearPendingNewQuestion,
    handleSaveSettings,
    handleOpenSettings,
    handleCloseSettings,
  } = useQuestionGroup({ section, subsection, group });

  const handleMoveGroup = useCallback(
    (fromIndex: number, toIndex: number) => moveGroup(section.id, subsection.id, fromIndex, toIndex),
    [moveGroup, section.id, subsection.id],
  );

  const { itemRef, handleRef, isDragging } = useSortableItem({
    type: DND_TYPES.QUESTION_GROUP,
    id: group.id,
    index,
    label: group.title || 'Untitled Group',
    onMove: handleMoveGroup,
    containerId: subsection.id,
  });

  return (
    <div
      ref={itemRef}
      className={cn(
        'rounded-md border p-3 transition-all',
        isDragging
          ? 'border-2 border-dashed border-blue-300 bg-blue-50/50 opacity-50'
          : isSelected
            ? 'border-primary-400 bg-primary-50/50 ring-primary-200 ring-1'
            : 'border-gray-100 bg-gray-50/50 hover:border-gray-200',
      )}
      onClick={handleClick}
    >
      <div className="mb-3 flex items-start gap-2">
        <div
          ref={handleRef}
          className="mt-1 shrink-0 cursor-grab active:cursor-grabbing"
          onClick={e => e.stopPropagation()}
        >
          <GripVertical className="size-3.5 text-gray-400" />
        </div>
        <div className="flex-1 space-y-1">
          <div
            className={cn(
              'text-base font-medium',
              group.title ? 'text-gray-950' : 'italic text-gray-400',
            )}
          >
            {group.title || 'Untitled Group'}
          </div>

          {group.description && <div className="text-sm text-gray-700">{group.description}</div>}

          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
            {group.format && group.format !== 'default' && (
              <Badge
                variant="secondary"
                className="capitalize"
              >
                {group.format}
              </Badge>
            )}
            {group.hidden && <Badge variant="warning">Hidden</Badge>}
            {hasCondition && group.conditional && (
              <Tooltip>
                <TooltipTrigger className="cursor-default">
                  <Badge variant="success">Conditions</Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs p-2">
                  {formatConditionalBehaviorText(group.conditional, questions)}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={handleOpenSettings}
            title="Group Settings"
          >
            <Settings className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={event => {
              event.stopPropagation();
              removeGroup(section.id, subsection.id, group.id);
            }}
            title="Delete Group"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className={cn(formatClasses, 'mb-3')}>
        {group.questions.map((questionRef, questionIndex) => {
          const question = questions[questionRef];
          if (!question) return null;

          return (
            <BuilderQuestionField
              key={questionRef}
              section={section}
              subsection={subsection}
              group={group}
              question={question}
              index={questionIndex}
              autoOpenSettings={pendingNewQuestionRef === questionRef}
              onSettingsAutoOpened={handleClearPendingNewQuestion}
            />
          );
        })}
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={handleAddQuestion}
      >
        <Plus className="mr-1 h-3 w-3" />
        Add Question
      </Button>

      <QuestionGroupSettingsDialog
        open={showSettings}
        group={group}
        questions={questions}
        onSave={handleSaveSettings}
        onClose={handleCloseSettings}
      />
    </div>
  );
}
